// Single front-end analytics/APM chokepoint — mirrors nexus_erp's
// lib/analytics/index.ts, and the sendEmail() / provider-abstraction discipline
// elsewhere in this repo. All init, gating, identify, track, and reset live
// here; components never touch the Clarity / Application Insights SDKs directly.
//
// Two tools, one seam (ADR-014): Microsoft Clarity (session replay + heatmaps —
// "how are customers using the portal") and Azure Application Insights browser
// RUM (page views + custom events + request correlation). Both are inert until
// their runtime key is present, so local dev (no keys served) runs with
// analytics fully off and no code branch.
//
// PII / data minimization (enforced here): identify sends only an OPAQUE user id
// plus the ERP-resolved tier code — never the customer's email, name, company,
// or any quote data. Clarity's default input/text masking stays ON. Two session
// classes are suppressed from all capture:
//   1. Test accounts (test.*@test.com) — so E2E runs never pollute metrics.
//   2. Active EMPLOYEE IMPERSONATION sessions (ADR-008) — an Argo rep driving a
//      customer's account is not customer usage, and must not be replayed as if
//      it were the customer. This is the portal analogue of Nexus's test-account
//      gate.

import Clarity from '@microsoft/clarity';
import { ApplicationInsights, type ITelemetryItem } from '@microsoft/applicationinsights-web';
import { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import type { CustomerUserDto } from 'shared';

export interface AnalyticsConfig {
  clarityProjectId?: string | null;
  appInsightsConnectionString?: string | null;
}

// Custom-event taxonomy. Names are stable and snake_case; role/tier are attached
// automatically (initializer), so callers pass only event-specific props and
// never customer PII. Extend as product questions arrive.
export const ANALYTICS_EVENTS = {
  quote_created: 'quote_created',
  quote_priced: 'quote_priced',
  quote_cloned: 'quote_cloned',
  quote_submitted: 'quote_submitted',
  // DECLARED WITH NO EMIT SITE, and it is not an oversight to fix by adding one
  // here. `POST /quotes/:id/request-signature` exists server-side and the SPA
  // has never called it — signature is initiated at submit, not by the customer
  // — so there is no moment in the front end at which this is true. Wire that
  // route or drop this name; a taxonomy with an entry nobody emits stops being
  // readable as "what the portal measures".
  signature_requested: 'signature_requested',
  payment_started: 'payment_started',
  login_succeeded: 'login_succeeded',
  register_succeeded: 'register_succeeded',
} as const;

export type AnalyticsEvent = keyof typeof ANALYTICS_EVENTS;

const FRONTEND_CLOUD_ROLE = 'print-portal-frontend';

let clarityStarted = false;
let appInsights: ApplicationInsights | null = null;
let reactPlugin: ReactPlugin | null = null;
// Set at identify, cleared at reset; stamped onto every App Insights item by the
// init-time telemetry initializer so page views + events carry the tier.
let currentTags: { tier: string } | null = null;
// Session-level kill switch for test accounts / impersonation sessions — no
// attributed telemetry is sent while it's on. Clarity has no stop API, so its
// exposure is limited to identity tags (never set for a suppressed session).
let suppressed = false;

// test.*@test.com users drive the Playwright/E2E suites (which hard-delete their
// data). Suppress them so test runs never pollute metrics.
export function isTestAccount(email?: string | null): boolean {
  return !!email && /^test\..+@test\.com$/i.test(email);
}

// The App Insights React plugin instance, once initialized — for withAITracking
// / AppInsightsErrorBoundary / useTrackEvent should a component want them.
export function getReactPlugin(): ReactPlugin | null {
  return reactPlugin;
}

// Idempotent: no-ops on missing keys and never re-initializes a tool already up.
export function initAnalytics(config: AnalyticsConfig): void {
  const clarityProjectId = config.clarityProjectId || '';
  const connectionString = config.appInsightsConnectionString || '';

  if (clarityProjectId && !clarityStarted) {
    Clarity.init(clarityProjectId);
    clarityStarted = true;
  }

  if (connectionString && !appInsights) {
    reactPlugin = new ReactPlugin();
    const instance = new ApplicationInsights({
      config: {
        connectionString,
        extensions: [reactPlugin],
        // Router-agnostic: page views are tracked from a useLocation effect
        // (AnalyticsBootstrap), which avoids the history-package coupling that
        // enableAutoRouteTracking needs and react-router v6 doesn't provide.
        enableAutoRouteTracking: false,
        // USB / disc content goes browser-direct to blob storage (ADR-021): each
        // request URL carries the customer's file path and a SAS, and a 20,000-file
        // folder is tens of thousands of requests. None of it is telemetry.
        excludeRequestFromAutoTrackingPatterns: [
          /\.blob\.core\.windows\.net\//i,
          /\/api\/storage\/mock\/media\//i,
        ],
      },
    });
    instance.loadAppInsights();
    // Stamp cloud role on every item, and the tier whenever a user is identified
    // — so ALL telemetry (page views, events, exceptions), not just custom
    // events, is filterable and separable from the backend role.
    instance.addTelemetryInitializer((item: ITelemetryItem) => {
      item.tags = item.tags || {};
      item.tags['ai.cloud.role'] = FRONTEND_CLOUD_ROLE;
      if (currentTags) {
        item.data = item.data || {};
        item.data.tier = currentTags.tier;
      }
    });
    appInsights = instance;
  }
}

// Attribute the session to an OPAQUE user id + tier ONLY — never the customer's
// email/name/company or any quote data. Skipped (and the session suppressed) for
// test accounts and active employee-impersonation sessions.
export function identifyUser(
  user: CustomerUserDto | null | undefined,
  opts: { impersonating: boolean },
): void {
  if (!user) return;
  if (opts.impersonating || isTestAccount(user.email)) {
    suppressed = true;
    currentTags = null;
    return;
  }
  suppressed = false;
  const userId = typeof user.id === 'string' ? user.id : '';
  if (!userId) return;
  const tier = user.tier_applied_code || '';
  currentTags = { tier };

  if (clarityStarted) {
    Clarity.identify(userId);
    Clarity.setTag('user_id', userId);
    if (tier) Clarity.setTag('tier', tier);
  }
  if (appInsights) {
    appInsights.setAuthenticatedUserContext(userId);
  }
}

// Clear identity on logout AND on stop-impersonation.
export function resetAnalytics(): void {
  suppressed = false;
  currentTags = null;
  if (appInsights) appInsights.clearAuthenticatedUserContext();
  // Clarity exposes no identity-reset API; its custom id clears with the session.
}

export function trackPageView(name?: string, uri?: string): void {
  if (!appInsights || suppressed) return;
  appInsights.trackPageView({ name, uri });
}

export function trackEvent(name: AnalyticsEvent, properties?: Record<string, unknown>): void {
  if (!appInsights || suppressed) return;
  appInsights.trackEvent({ name }, properties);
}

// The three AUTH events, which cannot be gated by `suppressed`.
//
// `suppressed` is set by identifyUser, and identifyUser runs from an effect keyed
// on the auth context. Every other event fires long after that; these three fire
// at the exact moment a session is created — register has no session at all yet,
// and login/callback emit in the same microtask as the await that signs the user
// in, before React has re-rendered. So at the one moment the flag would matter it
// still holds the PREVIOUS session's value, and a `test.*@test.com` account
// driving the E2E suite emits `register_succeeded` and `login_succeeded` into
// real metrics — which is precisely what the test-account gate exists to stop.
//
// The email is therefore passed in rather than read from state, and the session
// is suppressed from here on when it is a test account, so the rest of the run is
// covered without waiting for identify.
export function trackAuthEvent(
  name: AnalyticsEvent,
  email: string | null | undefined,
  properties?: Record<string, unknown>,
): void {
  if (isTestAccount(email)) {
    suppressed = true;
    currentTags = null;
    return;
  }
  trackEvent(name, properties);
}
